// Store
import { store } from "src/store";
import { networkStatusChanged } from "src/store/context/reducer";

// Types
import { NetworkStatus, type NetworkState } from "src/types";

// Services
import { logDebug } from "src/services/logger";

type WsInstance = {
  registerMetricListener: (cb: (metric: WsMetric) => void) => () => void;
};

type WsMetric = { rtt: number | null; reconnectCount: number };

interface NetworkInformation extends EventTarget {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
}

// => Thresholds
const RTT_UNSTABLE_MS = 300;
const UNSTABLE_EFFECTIVE_TYPES = ["slow-2g", "2g", "3g"];
const MAX_RECONNECTS_IN_WINDOW = 3;
const RECONNECT_WINDOW_MS = 30 * 1000;
const DEBOUNCE_MS = 2000;
const DOWNGRADE_SAMPLES = 2;

// => Internal state
let unsubscribeMetric: (() => void) | null = null;
let started = false;

let online = typeof navigator !== "undefined" ? navigator.onLine : true;
let effectiveType: string | null = null;
let lastRtt: number | null = null;
let reconnectCount = 0;
let reconnectTimestamps: Array<number> = [];
let unstableSamples = 0;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let currentStatus: NetworkStatus | null = null;

const getConnection = (): NetworkInformation | null => {
  if (typeof navigator === "undefined") return null;

  const nav = navigator as Navigator & { connection?: NetworkInformation };
  return nav.connection ?? null;
};

const isUnstable = (): boolean => {
  if (effectiveType && UNSTABLE_EFFECTIVE_TYPES.includes(effectiveType)) return true;

  if (lastRtt !== null && lastRtt > RTT_UNSTABLE_MS) return true;

  const now = Date.now();
  const withinWindow = reconnectTimestamps.filter((t) => now - t <= RECONNECT_WINDOW_MS);
  reconnectTimestamps = withinWindow;

  return withinWindow.length >= MAX_RECONNECTS_IN_WINDOW;
};

const classify = (): NetworkStatus => {
  if (!online) return NetworkStatus.OFFLINE;
  if (isUnstable()) return NetworkStatus.UNSTABLE;

  return NetworkStatus.ONLINE;
};

const dispatchStatus = (status: NetworkStatus) => {
  if (currentStatus === status) return;

  currentStatus = status;

  const network: NetworkState = {
    status,
    rtt: lastRtt,
    effectiveType,
    reconnectCount,
    lastChangedAt: Date.now(),
  };

  store.dispatch(networkStatusChanged(network));

  logDebug("Network: status atualizado", { status: NetworkStatus[status], rtt: lastRtt, effectiveType: effectiveType ?? null, reconnectCount });
};

const evaluate = () => {
  const target = classify();

  // Downgrade exige amostras consecutivas; upgrade é imediato
  if (target === NetworkStatus.OFFLINE) {
    unstableSamples = 0;
    dispatchStatus(NetworkStatus.OFFLINE);
    return;
  }

  if (target === NetworkStatus.UNSTABLE) {
    unstableSamples += 1;

    if (unstableSamples >= DOWNGRADE_SAMPLES) {
      dispatchStatus(NetworkStatus.UNSTABLE);
    }

    return;
  }

  // ONLINE
  unstableSamples = 0;
  dispatchStatus(NetworkStatus.ONLINE);
};

const schedule = () => {
  if (debounceTimer) clearTimeout(debounceTimer);

  debounceTimer = setTimeout(evaluate, DEBOUNCE_MS);
};

const onOnline = () => {
  online = true;
  schedule();
};

const onOffline = () => {
  online = false;
  schedule();
};

const onConnectionChange = () => {
  const connection = getConnection();

  effectiveType = connection?.effectiveType ?? null;

  schedule();
};

const onWsMetric = (metric: WsMetric) => {
  lastRtt = metric.rtt ?? lastRtt;

  if (metric.reconnectCount > reconnectCount) {
    if (metric.reconnectCount - reconnectCount >= 1) reconnectTimestamps.push(Date.now());
    reconnectCount = metric.reconnectCount;
  }

  schedule();
};

export function startNetworkMonitor(ws: WsInstance) {
  if (started) return;

  started = true;

  // Initial values
  online = navigator.onLine;
  effectiveType = getConnection()?.effectiveType ?? null;

  // DOM events
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  // Network Information API (Chromium)
  const connection = getConnection();
  if (connection?.addEventListener) {
    connection.addEventListener("change", onConnectionChange);
  }

  // WS metrics
  if (ws?.registerMetricListener) {
    unsubscribeMetric = ws.registerMetricListener(onWsMetric);
  }

  // First evaluation
  evaluate();
}

export function stopNetworkMonitor() {
  if (!started) return;

  started = false;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;

  window.removeEventListener("online", onOnline);
  window.removeEventListener("offline", onOffline);

  const connection = getConnection();
  if (connection?.removeEventListener) {
    connection.removeEventListener("change", onConnectionChange);
  }

  if (unsubscribeMetric) {
    unsubscribeMetric();
    unsubscribeMetric = null;
  }

  currentStatus = null;
  unstableSamples = 0;
}